import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box, Grid, Paper, Typography, Button, Chip, Divider, Stack, Alert,
  Tabs, Tab, TextField,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import DescriptionIcon from '@mui/icons-material/Description';
import LocalHospitalIcon from '@mui/icons-material/LocalHospital';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import SendIcon from '@mui/icons-material/Send';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import HandshakeIcon from '@mui/icons-material/Handshake';
import NoteAddIcon from '@mui/icons-material/NoteAdd';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { useCoordinateContext } from '~/hooks/coordinate/useCoordinateContext';
import CasePageHeader, { ActionKey } from '~/components/coordinate/patient-detail/CasePageHeader';
import InteractionControls from '~/components/coordinate/patient-detail/InteractionControls';
import ProviderMatchList from '~/components/coordinate/patient-detail/ProviderMatchList';
import CommitConfirmFlow from '~/components/coordinate/patient-detail/CommitConfirmFlow';
import ReferralPacketViewer from '~/components/coordinate/patient-detail/ReferralPacketViewer';
import InsurancePanel from '~/components/coordinate/patient-detail/InsurancePanel';
import EHRNoteDialog from '~/components/coordinate/patient-detail/EHRNoteDialog';
import CaseActivityFeed from '~/components/coordinate/patient-detail/CaseActivityFeed';
import CaseTaskPanel, { relativeDueDate } from '~/components/coordinate/patient-detail/CaseTaskPanel';
import PatientFamilyPanel from '~/components/coordinate/patient-detail/PatientFamilyPanel';
import { BARRIER_LABELS, BARRIER_COLORS } from '~/components/coordinate/patient-detail/BarriersPanel';
import ProviderNegotiationThread from '~/components/coordinate/patient-detail/ProviderNegotiationThread';
import PreSendReviewDialog from '~/components/coordinate/patient-detail/PreSendReviewDialog';
import AgentDraftCard from '~/components/coordinate/patient-detail/AgentDraftCard';
import AgentInsightBanner, { computeInsights } from '~/components/coordinate/patient-detail/AgentInsightBanner';
import ProviderOutreachPanel from '~/components/coordinate/patient-detail/ProviderOutreachPanel';
import { CoordinatePatient, DispositionType, ClinicalNeed, UrgencyLevel, ActivityEntry, CaseTask, PatientContext, NegotiationCondition } from '~/types/coordinate';

type WorkspaceTab = 'providers' | 'summary' | 'clinical' | 'tasks' | 'activity';

// Banners and other helpers refer to legacy 'insurance'/'family' tabs; remap those to the merged 'summary' tab.
const remapTab = (tab: string): WorkspaceTab =>
  tab === 'insurance' || tab === 'family' ? 'summary' : (tab as WorkspaceTab);

// Tab label with optional count badge
const TabLabel: React.FC<{ label: string; count?: number; color?: 'warning' | 'error' | 'default' }> = ({ label, count, color = 'default' }) => {
  const dotColor = color === 'error' ? '#b71c1c' : '#153873';
  return (
    <Stack direction="row" alignItems="center" spacing={0.75} component="span">
      <span>{label}</span>
      {count != null && count > 0 && (
        <Box
          component="span"
          sx={{
            display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
            width: 18, height: 18, borderRadius: '50%',
            bgcolor: dotColor, color: '#fff',
            fontSize: '0.62rem', fontWeight: 700, flexShrink: 0,
          }}
        >
          {count}
        </Box>
      )}
    </Stack>
  );
};

const CoordinatePatientDetail: React.FC = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const navigate = useNavigate();
  const {
    getPatientById,
    runMatching,
    selectProvider,
    confirmProvider,
    simulateCapacityChange,
    sendReferral,
    simulateReferralResponse,
    updateDisposition,
    updateClinicalNeeds,
    updateUrgency,
    addActivityEntry,
    addTask,
    updateTask,
    updatePatientContext,

    resolveBarrier,
    updateNegotiationCondition,
    acceptNegotiation,
    useAgentDraft,
    editAgentDraft,
    dismissAgentDraft,
    startProviderOutreach,
    selectProviderFromOutreach,
    confirmStartOfCare,
  } = useCoordinateContext();

  const patient = patientId ? getPatientById(patientId) : undefined;
  const [ehrDialogOpen, setEhrDialogOpen] = useState(false);
  const [packetDialogOpen, setPacketDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<WorkspaceTab>('providers');
  const [socDateInput, setSocDateInput] = useState('');
  const [highlightedTaskIds, setHighlightedTaskIds] = useState<string[] | undefined>(undefined);
  const [preSendOpen, setPreSendOpen] = useState(false);

  useEffect(() => {
    const main = document.querySelector('main');
    if (main) main.scrollTop = 0;
  }, [patientId]);

  if (!patient) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="error">Patient not found.</Typography>
        <Button onClick={() => navigate('/coordinate/patients')} startIcon={<ArrowBackIcon />} sx={{ mt: 1 }}>
          Back to Patients
        </Button>
      </Box>
    );
  }

  const { coordinationStatus, selectedProviderId, matchResults, referralPacket } = patient;
  const selectedProvider = matchResults?.find(m => m.provider.id === selectedProviderId)?.provider;

  // Show full provider list only while actively matching/selecting
  const showProviderList = ['matching', 'matched', 'confirming', 'confirm_failed'].includes(coordinationStatus);
  // Once confirmed or further, show only the selected provider (with a change option)
  const showSelectedProviderOnly = ['confirmed', 'awaiting_response', 'negotiating', 'accepted', 'declined'].includes(coordinationStatus) && !!selectedProvider;
  const showSendCTA = coordinationStatus === 'confirmed';
  const showPostSend = ['awaiting_response', 'negotiating', 'accepted', 'declined'].includes(coordinationStatus);

  // Show left InteractionControls panel only when the user needs to reconfigure and re-run matching.
  // All other states (matched and beyond) use 8+4 layout: primary content + secondary track sidebar.
  const showLeftPanel = ['pending', 'matching', 'confirm_failed', 'declined'].includes(coordinationStatus);

  const activeBarriers = (patient.barriers ?? []).filter(b => !b.resolved);
  const openTaskCount = (patient.tasks ?? []).filter(t => t.status !== 'done').length;
  const activityCount = patient.activityLog?.length ?? 0;
  const pendingDrafts = (patient.agentDrafts ?? []).filter(d => d.status === 'pending');
  const pendingProviderDrafts = pendingDrafts.filter(d => d.type === 'provider_outreach');
  const pendingFamilyDrafts = pendingDrafts.filter(d => d.type === 'family_update');
  const pendingPatientDrafts = pendingDrafts.filter(d => d.type === 'patient_explanation');
  const pendingSocDrafts = pendingDrafts.filter(d => d.type === 'start_of_care_followup');
  const pendingNarrativeDrafts = pendingDrafts.filter(d => d.type === 'agency_narrative');
  const ehrTask = (patient.tasks ?? []).find(t =>
    t.title.toLowerCase().includes('ehr') || t.title.toLowerCase().includes('discharge planning note')
  );
  const clinicalNeedsAttention = coordinationStatus === 'accepted' && ehrTask?.status !== 'done';

  // Badge signals — derived from same data as AgentInsightBanner insights
  const allInsights = computeInsights(patient);
  const familyBadgeCount =
    pendingFamilyDrafts.length + allInsights.filter(i => i.context === 'family').length;
  const insuranceBadgeCount = allInsights.filter(i => i.context === 'insurance').length;
  const clinicalBadgeCount =
    pendingPatientDrafts.length + pendingNarrativeDrafts.length + (clinicalNeedsAttention ? 1 : 0);
  const providersBadgeCount =
    pendingProviderDrafts.length + pendingSocDrafts.length + allInsights.filter(i => i.context === 'providers').length;

  const handleBannerAction = (key: ActionKey) => {
    if (key === 'run_matching') runMatching(patient.id);
    if (key === 'send_referral') setPreSendOpen(true);
    if (key === 'write_ehr') setEhrDialogOpen(true);
    if (key === 'contact_providers') startProviderOutreach(patient.id);
  };

  const handlePatientContextUpdate = (ctx: Partial<PatientContext>) => {
    if (ctx.consentDocumented === true && !patient.patientContext?.consentDocumented) {
      addActivityEntry(patient.id, {
        type: 'note',
        timestamp: new Date().toISOString(),
        author: patient.assignedTo,
        content: `Consent documented.${ctx.consentNotes ? ` Notes: ${ctx.consentNotes}` : ''}`,
        isAgent: false,
      });
    }
    updatePatientContext(patient.id, ctx);
  };

  const handleUseDraft = (draftId: string) => {
    const draft = patient.agentDrafts?.find(d => d.id === draftId);
    if (!draft) return;
    useAgentDraft(patient.id, draftId);
    const contact = patient.patientContext?.familyContacts?.[0];
    const channelLabel = draft.channel === 'portal' ? ' via EHR Patient Portal' : draft.channel === 'email' ? ' via email' : '';
    const draftActionLabels: Record<string, string> = {
      provider_outreach: `Provider outreach sent${channelLabel} to ${selectedProvider?.name ?? 'provider'} using Coordinate AI draft.`,
      patient_explanation: `Patient explanation letter sent${channelLabel} to ${patient.name} using Coordinate AI draft.`,
      family_update: `Family update sent${channelLabel} to ${contact?.name ?? 'family contact'} using Coordinate AI draft.`,
      agency_narrative: `Agency referral narrative sent to ${selectedProvider?.name ?? 'agency'} with referral packet using Coordinate AI draft.`,
      start_of_care_followup: `Start-of-care follow-up sent to ${selectedProvider?.name ?? 'agency'} requesting first visit confirmation.`,
    };
    const content = draftActionLabels[draft.type];
    if (content) {
      addActivityEntry(patient.id, {
        type: 'agent',
        timestamp: new Date().toISOString(),
        author: 'Coordinate AI',
        content,
        isAgent: true,
      });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Button
        onClick={() => navigate('/coordinate/patients')}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ mb: 2 }}
      >
        Back to Patients
      </Button>

      {/* Unified patient header — identity + status + next action */}
      <CasePageHeader
        patient={patient}
        onAction={handleBannerAction}
        onViewPacket={() => setPacketDialogOpen(true)}
      />

      {/* Active barriers — always visible, no heading, no toggle */}
      {activeBarriers.length > 0 && (
        <Box sx={{ mb: 2.5 }}>
          {activeBarriers.map(barrier => {
            const colors = BARRIER_COLORS[barrier.type];
            const linkedTask = barrier.linkedTaskId
              ? (patient.tasks ?? []).find(t => t.id === barrier.linkedTaskId)
              : undefined;
            const isAI = linkedTask?.isSystemGenerated === true;
            const isClickable = !!linkedTask;

            const handleBarrierClick = () => {
              if (!linkedTask) return;
              setActiveTab('tasks');
              setHighlightedTaskIds([linkedTask.id]);
            };

            return (
              <Stack
                key={barrier.id}
                direction="row"
                alignItems="center"
                spacing={1.5}
                onClick={isClickable ? handleBarrierClick : undefined}
                sx={{
                  px: 2, py: 0.875, mb: 0.5,
                  borderRadius: 1,
                  bgcolor: colors.bgcolor + '50',
                  borderLeft: '3px solid',
                  borderColor: colors.color,
                  cursor: isClickable ? 'pointer' : 'default',
                  '&:hover': isClickable ? { bgcolor: colors.bgcolor + '80' } : {},
                  transition: 'background-color 0.15s',
                }}
              >
                <Chip
                  label={BARRIER_LABELS[barrier.type]}
                  size="small"
                  sx={{ height: 17, fontSize: '0.62rem', fontWeight: 700, bgcolor: colors.bgcolor, color: colors.color, flexShrink: 0 }}
                />
                <Typography variant="body2" sx={{ fontSize: '0.82rem', flex: 1, minWidth: 0 }}>
                  {barrier.description}
                </Typography>

                {/* Right side: task status indicator or resolve button */}
                {linkedTask ? (
                  <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
                    {isAI ? (
                      <>
                        <AutoAwesomeIcon sx={{ fontSize: 13, color: '#635f99' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#635f99', fontWeight: 600, whiteSpace: 'nowrap' }}>
                          AI tracking
                        </Typography>
                      </>
                    ) : (
                      <>
                        <PersonOutlineIcon sx={{ fontSize: 14, color: '#668098' }} />
                        <Typography variant="caption" sx={{ fontSize: '0.7rem', color: '#668098', whiteSpace: 'nowrap' }}>
                          {linkedTask.assignee}
                          {linkedTask.dueDate && ` · ${relativeDueDate(linkedTask.dueDate).label.toLowerCase()}`}
                        </Typography>
                      </>
                    )}
                    <ArrowForwardIcon sx={{ fontSize: 13, color: 'text.disabled' }} />
                  </Stack>
                ) : (
                  <Button
                    size="small"
                    onClick={e => { e.stopPropagation(); resolveBarrier(patient.id, barrier.id); }}
                    sx={{ fontSize: '0.7rem', flexShrink: 0, color: 'text.secondary' }}
                  >
                    Resolve
                  </Button>
                )}
              </Stack>
            );
          })}
        </Box>
      )}

      {/* Track tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 3 }}>
        <Tabs value={activeTab} onChange={(_, v) => setActiveTab(v as WorkspaceTab)} sx={{ minHeight: 40 }}>
          <Tab value="providers" label={<TabLabel label="Placement" count={providersBadgeCount || undefined} color="warning" />} sx={{ fontSize: '0.8rem', minHeight: 40, py: 0.5 }} />
          <Tab value="summary" label={<TabLabel label="Summary" count={(insuranceBadgeCount + familyBadgeCount) || undefined} color="warning" />} sx={{ fontSize: '0.8rem', minHeight: 40, py: 0.5 }} />
          <Tab value="clinical" label={<TabLabel label="Clinical & Docs" count={clinicalBadgeCount || undefined} color="warning" />} sx={{ fontSize: '0.8rem', minHeight: 40, py: 0.5 }} />
          <Tab value="tasks" label={<TabLabel label="Tasks" count={openTaskCount} color="warning" />} sx={{ fontSize: '0.8rem', minHeight: 40, py: 0.5 }} />
          <Tab value="activity" label={<TabLabel label="Activity" count={activityCount} />} sx={{ fontSize: '0.8rem', minHeight: 40, py: 0.5 }} />
        </Tabs>
      </Box>

      {activeTab === 'providers' && (
        <Grid container spacing={3}>
          {/* Left column — Coordination controls (only shown when matching is relevant) */}
          {showLeftPanel && (
            <Grid item xs={12} md={3}>
              <InteractionControls
                patient={patient}
                onDispositionChange={(d: DispositionType) => updateDisposition(patient.id, d)}
                onNeedsChange={(needs: ClinicalNeed[]) => updateClinicalNeeds(patient.id, needs)}
                onUrgencyChange={(u: UrgencyLevel) => updateUrgency(patient.id, u)}
                onRunMatching={() => runMatching(patient.id)}
              />
            </Grid>
          )}

          {/* Primary content column — full width since track cards are now tabs */}
          <Grid item xs={12} md={showLeftPanel ? 9 : 12}>
            <Paper sx={{ p: 3, minHeight: 400 }}>
              {/* Provider-context AI insights */}
              <AgentInsightBanner patient={patient} onSwitchTab={tab => setActiveTab(remapTab(tab))} context="providers" />

              {/* SOC followup drafts */}
              {pendingSocDrafts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {pendingSocDrafts.map(draft => (
                    <AgentDraftCard
                      key={draft.id}
                      draft={draft}
                      onUse={() => handleUseDraft(draft.id)}
                      onEdit={content => editAgentDraft(patient.id, draft.id, content)}
                      onDismiss={() => dismissAgentDraft(patient.id, draft.id)}
                    />
                  ))}
                </Box>
              )}

              {/* Barriers are shown in the global strip above — no duplication needed here */}

              {/* Provider outreach AI drafts */}
              {pendingProviderDrafts.length > 0 && (
                <Box sx={{ mb: 2 }}>
                  {pendingProviderDrafts.map(draft => (
                    <AgentDraftCard
                      key={draft.id}
                      draft={draft}
                      onUse={() => handleUseDraft(draft.id)}
                      onEdit={content => editAgentDraft(patient.id, draft.id, content)}
                      onDismiss={() => dismissAgentDraft(patient.id, draft.id)}
                    />
                  ))}
                </Box>
              )}

              <CommitConfirmFlow coordinationStatus={coordinationStatus} selectedProviderName={selectedProvider?.name} />

              {/* Empty state — no matching started yet */}
              {coordinationStatus === 'pending' && (
                <Box
                  sx={{
                    display: 'flex', flexDirection: 'column', alignItems: 'center',
                    justifyContent: 'center', py: 5, px: 3, textAlign: 'center',
                    bgcolor: 'grey.50', borderRadius: 2,
                    border: '2px dashed', borderColor: 'grey.200', mb: 2,
                  }}
                >
                  <LocalHospitalIcon sx={{ fontSize: 40, color: 'grey.300', mb: 1.5 }} />
                  <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
                    Provider matches will appear here
                  </Typography>
                  <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 340 }}>
                    Set the disposition and clinical needs on the left, then run matching to see ranked post-acute providers for this patient.
                  </Typography>
                </Box>
              )}

              {/* Provider outreach panel — AI calling results */}
              {patient.providerOutreach && coordinationStatus === 'matched' && (
                <ProviderOutreachPanel
                  outreach={patient.providerOutreach}
                  selectedProviderId={patient.selectedProviderId}
                  onSelectProvider={providerId => selectProviderFromOutreach(patient.id, providerId)}
                  onConfirmProvider={() => confirmProvider(patient.id)}
                />
              )}

              {/* Once confirmed or further: show only the selected provider with a change option */}
              {showSelectedProviderOnly && selectedProvider && (
                <Box sx={{ mb: 2 }}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
                    <Typography variant="overline" color="text.secondary" sx={{ fontSize: '0.68rem', letterSpacing: 0.6 }}>
                      Selected Provider
                    </Typography>
                    {['confirmed', 'matched'].includes(coordinationStatus) && (
                      <Button
                        size="small"
                        variant="text"
                        startIcon={<SwapHorizIcon />}
                        onClick={() => runMatching(patient.id)}
                        sx={{ fontSize: '0.72rem', color: 'text.secondary' }}
                      >
                        Change Provider
                      </Button>
                    )}
                  </Stack>
                  {(() => {
                    const selectedMatch = matchResults?.find(m => m.provider.id === selectedProvider.id);
                    if (!selectedMatch) return null;
                    const { ProviderCard: _PC, ..._ } = { ProviderCard: null };
                    return (
                      <Paper variant="outlined" sx={{ p: 2.5, borderColor: 'primary.main', border: '2px solid' }}>
                        <Stack direction="row" alignItems="flex-start" justifyContent="space-between" sx={{ mb: 1 }}>
                          <Box>
                            <Typography variant="subtitle1" fontWeight={700}>{selectedProvider.name}</Typography>
                            <Typography variant="caption" color="text.secondary">{selectedProvider.distanceMiles} mi away</Typography>
                          </Box>
                          <Stack direction="row" spacing={1} alignItems="center">
                            <Box
                              component="span"
                              sx={{
                                px: 1, py: 0.25, borderRadius: 1,
                                bgcolor: coordinationStatus === 'accepted' ? '#e0f7f4' : coordinationStatus === 'declined' ? '#ffebee' : '#e5f6fb',
                                color: coordinationStatus === 'accepted' ? '#006e60' : coordinationStatus === 'declined' ? '#b71c1c' : '#153873',
                                fontSize: '0.72rem', fontWeight: 700,
                              }}
                            >
                              {coordinationStatus === 'accepted' ? 'Accepted' : coordinationStatus === 'declined' ? 'Declined' : coordinationStatus === 'awaiting_response' ? 'Awaiting Response' : coordinationStatus === 'negotiating' ? 'Negotiating' : 'Confirmed'}
                            </Box>
                          </Stack>
                        </Stack>
                        <Typography variant="caption" color="text.secondary">
                          {selectedProvider.distanceMiles} mi · Score {selectedMatch.totalScore} · {selectedMatch.fitLabel}
                        </Typography>
                      </Paper>
                    );
                  })()}
                </Box>
              )}

              {showProviderList && (
                <>
                  {coordinationStatus === 'matched' && !patient.providerOutreach && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: '#f0eef8', border: '1px solid', borderColor: 'rgba(99,95,153,0.2)', borderRadius: 1.5 }}>
                      <Stack direction="row" alignItems="center" justifyContent="space-between" flexWrap="wrap" useFlexGap spacing={1}>
                        <Box>
                          <Typography variant="body2" fontWeight={600} sx={{ mb: 0.25 }}>
                            Have Coordinate AI call the top 3 providers
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            AI calls providers simultaneously to check capacity — results appear here in real time.
                          </Typography>
                        </Box>
                        <Button
                          variant="contained"
                          size="small"
                          onClick={() => startProviderOutreach(patient.id)}
                          sx={{ bgcolor: '#635f99', '&:hover': { bgcolor: '#4a4776' }, flexShrink: 0 }}
                        >
                          Contact Top 3 Providers
                        </Button>
                      </Stack>
                    </Box>
                  )}
                  <ProviderMatchList
                    patient={patient}
                    onSelectAndConfirm={(providerId) => {
                      selectProvider(patient.id, providerId);
                      confirmProvider(patient.id);
                    }}
                    onSimulateCapacityChange={() => simulateCapacityChange(patient.id)}
                  />
                </>
              )}

              {/* Send CTA */}
              {showSendCTA && (
                <>
                  <Divider sx={{ my: 3 }} />
                  <Stack direction="row" justifyContent="flex-end">
                    <Button
                      variant="contained"
                      size="large"
                      startIcon={<SendIcon />}
                      onClick={() => setPreSendOpen(true)}
                      sx={{ px: 4, bgcolor: '#153873', '&:hover': { bgcolor: '#0b233f' } }}
                    >
                      Send Referral to {selectedProvider?.name ?? 'Provider'}
                    </Button>
                  </Stack>
                </>
              )}

              {showPostSend && (
                <>
                  {coordinationStatus === 'awaiting_response' && (
                    <>
                      <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Referral Sent · Awaiting Response from {selectedProvider?.name ?? 'Provider'}
                        </Typography>
                        {patient.referralSentAt && (
                          <Typography variant="caption">
                            Sent {new Date(patient.referralSentAt).toLocaleString()}
                          </Typography>
                        )}
                      </Alert>
                      <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                        <Button size="small" variant="outlined" startIcon={<ThumbUpIcon />}
                          onClick={() => simulateReferralResponse(patient.id, 'accepted')}
                          sx={{ color: '#006e60', borderColor: '#00bda1', '&:hover': { borderColor: '#006e60', bgcolor: 'rgba(0,189,161,0.04)' } }}>
                          Simulate: Accepted
                        </Button>
                        <Button size="small" variant="outlined" color="error" startIcon={<ThumbDownIcon />}
                          onClick={() => simulateReferralResponse(patient.id, 'declined')}>
                          Simulate: Declined
                        </Button>
                        <Button size="small" variant="outlined" startIcon={<HandshakeIcon />}
                          onClick={() => simulateReferralResponse(patient.id, 'negotiating')}
                          sx={{ color: '#635f99', borderColor: 'rgba(99,95,153,0.5)', '&:hover': { borderColor: '#635f99', bgcolor: 'rgba(99,95,153,0.04)' } }}>
                          Simulate: Conditions
                        </Button>
                      </Stack>
                    </>
                  )}

                  {coordinationStatus === 'negotiating' && patient.providerNegotiation && (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          {patient.providerNegotiation.providerName} responded with conditions
                        </Typography>
                        <Typography variant="body2">Resolve all conditions to confirm the placement.</Typography>
                      </Alert>
                      <ProviderNegotiationThread
                        negotiation={patient.providerNegotiation}
                        onUpdateCondition={(condId, updates) => updateNegotiationCondition(patient.id, condId, updates)}
                        onAccept={() => acceptNegotiation(patient.id)}
                      />
                    </>
                  )}

                  {coordinationStatus === 'accepted' && (
                    <>
                      <Alert severity="success" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight={600}>
                          Referral Accepted by {selectedProvider?.name ?? 'Provider'} — transitioning to care coordination.
                        </Typography>
                      </Alert>

                      {/* Start of care confirmation */}
                      <Box
                        sx={{
                          p: 2, mb: 2, borderRadius: 1.5,
                          border: '1px solid',
                          borderColor: patient.startOfCareDate ? 'success.light' : 'warning.light',
                          bgcolor: patient.startOfCareDate ? '#f1f8f1' : '#fffbf2',
                        }}
                      >
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <EventAvailableIcon sx={{ fontSize: 17, color: patient.startOfCareDate ? 'success.main' : 'warning.main' }} />
                          <Typography variant="body2" fontWeight={700}
                            sx={{ color: patient.startOfCareDate ? 'success.dark' : 'warning.dark' }}>
                            {patient.startOfCareDate
                              ? `Start of care confirmed — ${patient.startOfCareDate}`
                              : 'Start of care not yet confirmed'}
                          </Typography>
                        </Stack>
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: patient.startOfCareDate ? 0 : 1.5 }}>
                          {patient.startOfCareDate
                            ? 'First home visit date confirmed with the agency.'
                            : `Follow up with ${selectedProvider?.name ?? 'the agency'} to confirm the first home visit date before the patient is discharged.`}
                        </Typography>
                        {!patient.startOfCareDate && (
                          <Stack direction="row" spacing={1} alignItems="center">
                            <TextField
                              size="small"
                              placeholder="e.g. May 2, 2026 — 9 AM"
                              value={socDateInput}
                              onChange={e => setSocDateInput(e.target.value)}
                              sx={{ flex: 1, maxWidth: 260, '& .MuiInputBase-input': { fontSize: '0.82rem' } }}
                            />
                            <Button
                              size="small"
                              variant="contained"
                              disabled={!socDateInput.trim()}
                              sx={{ bgcolor: '#00bda1', '&:hover': { bgcolor: '#009e87' } }}
                              onClick={() => {
                                confirmStartOfCare(patient.id, socDateInput.trim());
                                setSocDateInput('');
                              }}
                            >
                              Confirm
                            </Button>
                          </Stack>
                        )}
                      </Box>

                      <Button variant="outlined" startIcon={<NoteAddIcon />} onClick={() => setEhrDialogOpen(true)} sx={{ mb: 2 }}>
                        Write Discharge Planning Note to EHR
                      </Button>
                    </>
                  )}

                  {coordinationStatus === 'declined' && (
                    <Alert severity="warning" sx={{ mb: 2 }}>
                      <Typography variant="body2" fontWeight={600}>
                        Referral Declined by {selectedProvider?.name ?? 'Provider'}.
                      </Typography>
                      <Typography variant="body2">
                        Use the controls on the left to run matching again and select a different provider.
                      </Typography>
                    </Alert>
                  )}
                </>
              )}

            </Paper>
          </Grid>

        </Grid>
      )}

      {activeTab === 'summary' && (
        <Stack spacing={3}>
          <Paper sx={{ p: 3 }}>
            <AgentInsightBanner patient={patient} onSwitchTab={tab => setActiveTab(remapTab(tab))} context="insurance" />
            <InsurancePanel patient={patient} />
          </Paper>
          <Paper sx={{ p: 3 }}>
            <AgentInsightBanner patient={patient} onSwitchTab={tab => setActiveTab(remapTab(tab))} context="family" />
            {pendingFamilyDrafts.length > 0 && (
              <Box sx={{ mb: 2 }}>
                {pendingFamilyDrafts.map(draft => (
                  <AgentDraftCard
                    key={draft.id}
                    draft={draft}
                    onUse={() => handleUseDraft(draft.id)}
                    onEdit={content => editAgentDraft(patient.id, draft.id, content)}
                    onDismiss={() => dismissAgentDraft(patient.id, draft.id)}
                  />
                ))}
              </Box>
            )}
            <PatientFamilyPanel
              patientContext={patient.patientContext}
              onUpdate={handlePatientContextUpdate}
            />
          </Paper>
        </Stack>
      )}

      {activeTab === 'clinical' && (
        <Paper sx={{ p: 3 }}>
          {pendingNarrativeDrafts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {pendingNarrativeDrafts.map(draft => (
                <AgentDraftCard
                  key={draft.id}
                  draft={draft}
                  onUse={() => handleUseDraft(draft.id)}
                  onEdit={content => editAgentDraft(patient.id, draft.id, content)}
                  onDismiss={() => dismissAgentDraft(patient.id, draft.id)}
                />
              ))}
            </Box>
          )}
          {pendingPatientDrafts.length > 0 && (
            <Box sx={{ mb: 2 }}>
              {pendingPatientDrafts.map(draft => (
                <AgentDraftCard
                  key={draft.id}
                  draft={draft}
                  onUse={() => handleUseDraft(draft.id)}
                  onEdit={content => editAgentDraft(patient.id, draft.id, content)}
                  onDismiss={() => dismissAgentDraft(patient.id, draft.id)}
                />
              ))}
            </Box>
          )}
          {patient.referralPacket ? (
            <>
              {coordinationStatus === 'accepted' && (
                <Box sx={{ mb: 3 }}>
                  <Button variant="outlined" startIcon={<NoteAddIcon />} onClick={() => setEhrDialogOpen(true)}>
                    Write Discharge Planning Note to EHR
                  </Button>
                </Box>
              )}
              <ReferralPacketViewer packet={patient.referralPacket} />
            </>
          ) : (
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 7, textAlign: 'center' }}>
              <DescriptionIcon sx={{ fontSize: 48, color: 'grey.300', mb: 1.5 }} />
              <Typography variant="subtitle1" fontWeight={600} color="text.secondary" sx={{ mb: 0.5 }}>
                Clinical & Docs
              </Typography>
              <Typography variant="body2" color="text.disabled" sx={{ maxWidth: 380 }}>
                Once a provider is confirmed, Coordinate AI assembles the referral packet here — including the discharge summary, physician orders, insurance authorization, and clinical narrative. AI-drafted letters (agency narrative, patient explanation) will also appear here for review before sending.
              </Typography>
            </Box>
          )}
        </Paper>
      )}

      {activeTab === 'tasks' && (
        <Paper sx={{ p: 3 }}>
          <CaseTaskPanel
            tasks={patient.tasks ?? []}
            onAddTask={task => addTask(patient.id, task)}
            onUpdateTask={(taskId, updates) => updateTask(patient.id, taskId, updates)}
            highlightedTaskIds={highlightedTaskIds}
          />
        </Paper>
      )}

      {activeTab === 'activity' && (
        <Paper sx={{ p: 3 }}>
          <CaseActivityFeed
            patientId={patient.id}
            entries={patient.activityLog ?? []}
            onAddEntry={entry => addActivityEntry(patient.id, entry)}
          />
        </Paper>
      )}

      {selectedProvider && (
        <EHRNoteDialog
          open={ehrDialogOpen}
          onClose={() => setEhrDialogOpen(false)}
          patient={patient}
          providerName={selectedProvider.name}
        />
      )}

      <PreSendReviewDialog
        open={preSendOpen}
        onClose={() => setPreSendOpen(false)}
        onSend={() => { setPreSendOpen(false); sendReferral(patient.id); }}
        patient={patient}
        providerName={selectedProvider?.name ?? 'Provider'}
        onNavigate={(tab, taskIds) => { setPreSendOpen(false); setActiveTab(remapTab(tab)); if (taskIds?.length) setHighlightedTaskIds(taskIds); }}
      />

      {/* Referral packet quick-view dialog */}
      {referralPacket && packetDialogOpen && (
        <Box
          sx={{
            position: 'fixed', inset: 0, zIndex: 1300,
            bgcolor: 'rgba(0,0,0,0.5)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            p: 3,
          }}
          onClick={() => setPacketDialogOpen(false)}
        >
          <Box
            sx={{ bgcolor: '#fff', borderRadius: 2, p: 3, maxWidth: 760, width: '100%', maxHeight: '85vh', overflowY: 'auto' }}
            onClick={e => e.stopPropagation()}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Typography variant="h6" fontWeight={700}>Referral Packet</Typography>
              <Button size="small" onClick={() => setPacketDialogOpen(false)}>Close</Button>
            </Stack>
            <ReferralPacketViewer packet={referralPacket} />
          </Box>
        </Box>
      )}
    </Box>
  );
};

export default CoordinatePatientDetail;
